import { ZDropdownButton } from "@/shared/components/button/DropdownButton";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import clsx from "clsx";
import { MutableRefObject, useCallback, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import QuestionItem, { QUESTION_TYPES } from "./QuestionItem";
import {
  buildQuestionTree,
  convertChildrenToRootQuestions,
  getQuestionDisplayNumber,
  moveQuestionSubtree,
  moveUnmappedQuestionAfterSiblings,
  reorderQuestionsAfterMapping,
  type Question,
} from "./questionTreeUtils";

interface QuestionListProps {
  invalidQuestionIds?: Set<string>;
  duplicateOptionsByQuestion?: Map<string, Set<number>>;
  onQuestionEdit?: (questionId: string) => void;
  questionRefs?: MutableRefObject<Map<string, HTMLDivElement>>;
  isExtractingQuestions?: boolean;
  hideComments?: boolean;
  hideSubQuestionMapping?: boolean;
  showFileUploadType?: boolean;
}

const QuestionList = ({
  invalidQuestionIds = new Set(),
  duplicateOptionsByQuestion = new Map(),
  onQuestionEdit,
  questionRefs,
  isExtractingQuestions = false,
  hideComments = false,
  hideSubQuestionMapping = false,
  showFileUploadType = false,
}: QuestionListProps) => {
  const { control, watch, setValue } = useFormContext();
  const { append, remove, replace } = useFieldArray({
    control,
    name: "questions",
  });

  const questionnaireId = watch("questionnaireId");
  const allQuestions = watch("questions") || [];

  // Build tree and calculate display numbers
  const questionTree = useMemo(() => buildQuestionTree(allQuestions as Question[]), [allQuestions]);

  // Get display number for a question using the tree
  const getDisplayNumberForQuestion = (questionId: string): string => {
    return getQuestionDisplayNumber(allQuestions as Question[], questionId);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const draggedQuestion = allQuestions.find((q: any) => q.id === active.id);

      // Only allow dragging root questions (questions without parents)
      if (draggedQuestion?.parent_question_id) {
        // Silently prevent dragging sub-questions
        return;
      }

      console.log(
        "Before reorder:",
        allQuestions.map((q: any) => q.id),
      );
      console.log("Dragging:", active.id, "to:", over.id);

      // Move the entire subtree (parent + all children) together
      const reorderedQuestions = moveQuestionSubtree(
        allQuestions as Question[],
        active.id as string,
        over.id as string,
      );

      console.log(
        "After reorder:",
        reorderedQuestions.map((q: any) => q.id),
      );

      // Update the entire questions array using replace for proper re-render
      replace(reorderedQuestions);
    }
  };

  const handleAddQuestion = useCallback(
    (question_type: string) => {
      // Determine actual question type
      const actualQuestionType = question_type === "dropdown" ? "single_choice" : question_type;
      const isDropdown = question_type === "dropdown";

      // Generate a UUID for the new question
      const questionId = uuidv4();

      // Add question to form state directly (no API call)
      const newQuestion: any = {
        id: questionId,
        question: "",
        question_type: actualQuestionType as "free_text" | "single_choice" | "multiple_choice" | "file_upload",
        response_choices: actualQuestionType === "free_text" || actualQuestionType === "file_upload" ? [] : [""],
        comments_allowed: false,
        is_dropdown: isDropdown,
        required: true,
        parent_question_id: undefined,
        parent_question_response_option_index: undefined,
        question_number: "",
      };

      // Add max_files for file_upload type
      if (actualQuestionType === "file_upload") {
        newQuestion.max_files = 5; // Default value
      }

      append(newQuestion);
    },
    [append],
  );

  const handleDuplicateQuestion = (questionData: any) => {
    // Generate a UUID for the duplicated question
    const questionId = uuidv4();

    // Add duplicated question to form state directly (no API call)
    // Duplicated question is always a root question (no parent)
    const newQuestion: any = {
      id: questionId,
      question: questionData.question,
      question_type: questionData.question_type,
      response_choices: questionData.response_choices || [],
      comments_allowed: questionData.comments_allowed,
      is_dropdown: questionData.is_dropdown || false,
      required: questionData.required,
      parent_question_id: undefined,
      parent_question_response_option_index: undefined,
      question_number: "",
    };

    // Copy max_files for file_upload type
    if (questionData.question_type === "file_upload" && questionData.max_files !== undefined) {
      newQuestion.max_files = questionData.max_files;
    }

    append(newQuestion);
  };

  const handleDeleteQuestion = (questionIndex: number) => {
    const questions = watch("questions") as Question[];
    const deletedQuestion = questions[questionIndex];

    if (!deletedQuestion) return;

    // Convert children to root questions before deleting parent
    const updatedQuestions = convertChildrenToRootQuestions(questions, deletedQuestion.id);

    // Set the updated questions array (this converts children to root)
    setValue("questions", updatedQuestions, { shouldDirty: true });

    // Now remove the deleted question
    remove(questionIndex);
  };

  const handleSubQuestionMap = (parentQuestionId: string, optionIndex: number, subQuestionId: string | null) => {
    const questions = watch("questions") as Question[];
    const parentIdx = questions.findIndex((q) => q.id === parentQuestionId);
    const subIdx = subQuestionId ? questions.findIndex((q) => q.id === subQuestionId) : -1;

    if (parentIdx === -1) return;

    if (subQuestionId && subIdx !== -1) {
      // Map sub-question to parent option
      // For text fields (optionIndex = -1), parent_question_response_option_index should be null
      const parentOptionIndex = optionIndex === -1 ? null : optionIndex;

      // Create updated questions array with the new mapping
      const updatedQuestions = questions.map((q, idx) => {
        if (idx === subIdx) {
          return {
            ...q,
            parent_question_id: parentQuestionId,
            parent_question_response_option_index: parentOptionIndex,
          };
        }
        return q;
      });

      // Always reorder and sort children by option order
      const reorderedQuestions = reorderQuestionsAfterMapping(updatedQuestions, parentQuestionId, subQuestionId);

      // Update the entire questions array
      setValue("questions", reorderedQuestions, { shouldDirty: true });
    } else {
      // Remove sub-question mapping (convert to root question)
      // Find the sub-question that was mapped to this parent option by INDEX, not text
      const mappedSubIdx = questions.findIndex(
        (q) =>
          q.parent_question_id === parentQuestionId &&
          q.parent_question_response_option_index === (optionIndex === -1 ? null : optionIndex),
      );

      if (mappedSubIdx !== -1) {
        const unmappedQuestionId = questions[mappedSubIdx].id;

        // Create updated questions array with unmapped question
        const updatedQuestions = questions.map((q, idx) => {
          if (idx === mappedSubIdx) {
            return {
              ...q,
              parent_question_id: undefined,
              parent_question_response_option_index: undefined,
            };
          }
          return q;
        });

        // Move unmapped question after all siblings
        const reorderedQuestions = moveUnmappedQuestionAfterSiblings(
          updatedQuestions,
          unmappedQuestionId,
          parentQuestionId,
        );

        // Update the entire questions array
        setValue("questions", reorderedQuestions, { shouldDirty: true });
      }
    }
  };

  // Prepare dropdown options from QUESTION_TYPES
  const dropdownOptions = useMemo(
    () =>
      QUESTION_TYPES.filter((qt) => showFileUploadType || qt.value !== "file_upload").map((qt) => ({
        key: qt.value,
        label: qt.label,
        icon: qt.icon,
        showDividerAfter: qt.showDividerAfter,
        showDividerBefore: qt.showDividerBefore,
        onClick: () => handleAddQuestion(qt.value),
      })),
    [showFileUploadType, handleAddQuestion],
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row justify-end items-center mb-6">
        <div className={clsx("flex gap-2 mt-2 relative w-full", { "justify-end": hideSubQuestionMapping })}>
          <h3
            className={clsx("text-sm font-semibold text-[#18181B] items-center justify-between w-full", {
              hidden: hideSubQuestionMapping,
            })}
          >
            Configure Questionnaire
          </h3>
          <ZTooltip
            content="Please wait while AI is extracting questions from the document"
            isDisabled={!isExtractingQuestions}
            placement="left"
            showArrow
          >
            <div className="flex justify-between items-center">
              <ZDropdownButton
                label="Add Question"
                className="text-xs py-1 gap-1 px-2"
                options={dropdownOptions}
                isDisabled={isExtractingQuestions}
                variant="bordered"
              />
            </div>
          </ZTooltip>
        </div>
      </div>

      <div className="grow overflow-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={allQuestions.map((q: any) => q.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-6 overflow-y-auto overflow-x-hidden size-full">
              {allQuestions.map((question: any, idx: number) => (
                <QuestionItem
                  key={question.id}
                  id={question.id}
                  index={idx}
                  onRemove={() => handleDeleteQuestion(idx)}
                  onDuplicate={handleDuplicateQuestion}
                  isInvalid={invalidQuestionIds.has(question.id)}
                  duplicateOptionIndices={duplicateOptionsByQuestion.get(question.id)}
                  onEdit={onQuestionEdit}
                  questionRef={questionRefs}
                  allQuestions={allQuestions}
                  onSubQuestionMap={handleSubQuestionMap}
                  getDisplayNumber={getDisplayNumberForQuestion}
                  hideComments={hideComments}
                  hideSubQuestionMapping={hideSubQuestionMapping}
                  showFileUploadType={showFileUploadType}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default QuestionList;
